import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Topbar from '../../components/common/Topbar';
import Toast from '../../components/common/Toast';
import { showToast } from '../../store/uiSlice';
import api from '../../services/api';

const Documents = () => {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadStatus, setUploadStatus] = useState({ id: 'not_uploaded', address: 'not_uploaded', bank: 'not_uploaded', selfie: 'not_uploaded' });
  const [verificationStatus, setVerificationStatus] = useState({ isVerified: false, idVerified: false, addressVerified: false, selfieVerified: false });

  useEffect(() => { fetchVerificationStatus(); }, []);

  const fetchVerificationStatus = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/documents/verification/status');
      const data = response.data;
      setVerificationStatus({ isVerified: data.isVerified || false, idVerified: data.idVerified || false, addressVerified: data.addressVerified || false, selfieVerified: data.selfieVerified || false });
      if (data.documents && Array.isArray(data.documents)) {
        const statusMap = { ...uploadStatus };
        data.documents.forEach(doc => { statusMap[doc.type] = doc.status || 'not_uploaded'; });
        setUploadStatus(statusMap);
      }
    } catch (error) { console.error('Failed to fetch verification status:', error); }
    finally { setIsLoading(false); }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('type', type);
    formData.append('document', file);
    setIsUploading(true);
    try {
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadStatus(prev => ({ ...prev, [type]: 'uploaded' }));
      dispatch(showToast({ message: `${getDocumentName(type)} uploaded!`, type: 'success' }));
      fetchVerificationStatus();
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message || 'Upload failed', type: 'error' }));
    } finally { setIsUploading(false); }
  };

  const getDocumentName = (type) => ({ id: 'Government ID', address: 'Proof of Address', bank: 'Bank Statement', selfie: 'Selfie with ID' }[type]);

  const getStatusBadge = (type) => {
    const status = uploadStatus[type];
    const verifiedMap = { id: verificationStatus.idVerified, address: verificationStatus.addressVerified, selfie: verificationStatus.selfieVerified };
    if (verifiedMap[type]) return { text: 'Verified', color: 'var(--color-green)', bg: 'var(--color-greenBg)', border: 'var(--color-greenBorder)', icon: 'fa-check-circle' };
    switch (status) {
      case 'approved': return { text: 'Approved', color: 'var(--color-green)', bg: 'var(--color-greenBg)', border: 'var(--color-greenBorder)', icon: 'fa-check-circle' };
      case 'pending': return { text: 'Pending Review', color: 'var(--color-yellow)', bg: 'var(--color-yellowBg)', border: 'var(--color-yellowBorder)', icon: 'fa-clock' };
      case 'uploaded': return { text: 'Ready to Submit', color: 'var(--color-blue)', bg: 'var(--color-blueBg)', border: 'var(--color-blueBorder)', icon: 'fa-cloud-upload-alt' };
      case 'rejected': return { text: 'Rejected', color: 'var(--color-red)', bg: 'var(--color-redBg)', border: 'var(--color-redBorder)', icon: 'fa-times-circle' };
      default: return { text: 'Not Uploaded', color: 'var(--color-textDim)', bg: 'var(--color-bgCard)', border: 'var(--color-borderMedium)', icon: 'fa-cloud-upload-alt' };
    }
  };

  const handleSubmitVerification = async () => {
    const requiredTypes = ['id', 'address', 'selfie'];
    const uploadedCount = requiredTypes.filter(t => uploadStatus[t] === 'uploaded' || uploadStatus[t] === 'pending').length;
    if (uploadedCount < requiredTypes.length) { dispatch(showToast({ message: `Please upload all required documents first (${uploadedCount}/${requiredTypes.length} uploaded)`, type: 'error' })); return; }
    setIsSubmitting(true);
    try {
      await api.post('/documents/submit');
      dispatch(showToast({ message: 'All documents submitted for verification!', type: 'success' }));
      fetchVerificationStatus();
    } catch (error) { dispatch(showToast({ message: error.response?.data?.message || 'Submission failed', type: 'error' })); }
    finally { setIsSubmitting(false); }
  };

  const documentsList = [
    { type: 'id', icon: 'fas fa-passport', name: 'Government ID', required: true },
    { type: 'address', icon: 'fas fa-file-alt', name: 'Proof of Address', required: true },
    { type: 'bank', icon: 'fas fa-bank', name: 'Bank Statement', required: false },
    { type: 'selfie', icon: 'fas fa-camera', name: 'Selfie with ID', required: true },
  ];

  const requiredTypes = ['id', 'address', 'selfie'];
  const allRequiredUploaded = requiredTypes.every(t => uploadStatus[t] === 'uploaded' || uploadStatus[t] === 'pending' || uploadStatus[t] === 'approved');
  const hasPendingDocs = Object.values(uploadStatus).some(s => s === 'pending');
  const allVerified = verificationStatus.idVerified && verificationStatus.addressVerified && verificationStatus.selfieVerified;
  const uploadedRequiredCount = requiredTypes.filter(t => uploadStatus[t] === 'uploaded' || uploadStatus[t] === 'pending' || uploadStatus[t] === 'approved').length;
  const canSubmit = allRequiredUploaded && !hasPendingDocs && !allVerified;

  const cardStyle = { background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-orange)', borderTopColor: 'transparent' }} />
          <p className="font-mono text-sm" style={{ color: 'var(--color-text)' }}>Loading documents…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{ background: 'radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)' }} />
      </div>

      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 flex items-center justify-center min-h-[calc(100vh-52px)]">
        <div className="w-full max-w-[700px]">

          <div className="text-center mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
              <i className="fas fa-file-shield text-[9px]" style={{ color: 'var(--color-text)' }} />
              <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-text)' }}>VERIFICATION</span>
            </div>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
              Upload <span style={{ color: 'var(--color-text)' }}>Documents</span>
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>Upload all required documents, then submit for verification</p>
          </div>

          {allVerified && (
            <div className="mb-6 p-4 rounded-xl flex items-center gap-3 animate-slide-up" style={{ background: 'var(--color-greenBg)', border: '1px solid var(--color-greenBorder)' }}>
              <i className="fas fa-check-circle text-2xl" style={{ color: 'var(--color-green)' }} />
              <div>
                <div className="font-syne text-sm font-bold" style={{ color: 'var(--color-green)' }}>Verification Successful!</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>Your account is fully verified.</div>
              </div>
            </div>
          )}

          {hasPendingDocs && !allVerified && (
            <div className="mb-6 p-4 rounded-xl flex items-center gap-3 animate-slide-up" style={{ background: 'var(--color-yellowBg)', border: '1px solid var(--color-yellowBorder)' }}>
              <i className="fas fa-clock text-2xl" style={{ color: 'var(--color-yellow)' }} />
              <div>
                <div className="font-syne text-sm font-bold" style={{ color: 'var(--color-yellow)' }}>Verification In Progress</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>Your documents are being reviewed.</div>
              </div>
            </div>
          )}

          {!allVerified && !hasPendingDocs && (
            <div className="mb-6 p-4 rounded-xl animate-slide-up" style={{ background: 'var(--color-blueBg)', border: '1px solid var(--color-blueBorder)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-syne font-bold" style={{ color: 'var(--color-text)' }}>Upload Progress</span>
                <span className="text-xs font-mono" style={{ color: 'var(--color-blue)' }}>{uploadedRequiredCount}/{requiredTypes.length} Required</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-borderMedium)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(uploadedRequiredCount / requiredTypes.length) * 100}%`, background: 'linear-gradient(to right, var(--color-blue), var(--color-purple))' }} />
              </div>
            </div>
          )}

          <div className="rounded-2xl overflow-hidden animate-slide-up animation-delay-100" style={cardStyle}>
            <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-id-card text-base" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>KYC Documents</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textDim)' }}>
                  {allVerified ? 'All documents verified' : hasPendingDocs ? 'Under review' : `${uploadedRequiredCount}/${requiredTypes.length} documents uploaded`}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documentsList.map((doc) => {
                  const isUploaded = uploadStatus[doc.type] === 'uploaded' || uploadStatus[doc.type] === 'pending' || uploadStatus[doc.type] === 'approved';
                  const isVerified = verificationStatus[doc.type + 'Verified'] || uploadStatus[doc.type] === 'approved';
                  const badge = getStatusBadge(doc.type);
                  return (
                    <div key={doc.type} className="rounded-xl p-5 text-center transition-all" style={{ background: 'var(--color-bgCard)', border: `1px solid ${isVerified ? 'var(--color-greenBorder)' : isUploaded ? 'var(--color-blueBorder)' : 'var(--color-borderMedium)'}` }}>
                      <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center text-xl mb-3"
                        style={{ background: isVerified ? 'var(--color-greenBg)' : isUploaded ? 'var(--color-blueBg)' : 'var(--color-orangeBg)', border: `1px solid ${badge.border}`, color: badge.color }}>
                        {isVerified ? <i className="fas fa-check-circle" /> : isUploaded ? <i className="fas fa-check" /> : <i className={doc.icon} />}
                      </div>
                      <div className="font-syne text-sm font-bold mb-1" style={{ color: 'var(--color-text)' }}>
                        {doc.name}{doc.required && <span className="ml-1 text-xs" style={{ color: 'var(--color-red)' }}>*</span>}
                      </div>
                      <div className="text-[9px] font-mono mt-2 mb-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded border" style={{ background: badge.bg, borderColor: badge.border, color: badge.color }}>
                          <i className={`fas ${badge.icon} text-[8px]`} /> {badge.text}
                        </span>
                      </div>
                      <input type="file" id={`upload-${doc.type}`} accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFileUpload(doc.type, e.target.files[0])} />
                      <button onClick={() => document.getElementById(`upload-${doc.type}`).click()} disabled={isUploading || isVerified || hasPendingDocs}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-syne text-[9px] font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: badge.bg, border: `1px solid ${badge.border}`, color: badge.color }}>
                        {isVerified ? <><i className="fas fa-check-circle text-[9px]" /> Verified</>
                          : isUploaded && !hasPendingDocs ? <><i className="fas fa-sync-alt text-[9px]" /> Re-upload</>
                          : isUploaded && hasPendingDocs ? <><i className="fas fa-clock text-[9px]" /> Under Review</>
                          : <><i className="fas fa-cloud-upload-alt text-[9px]" /> Upload {doc.required ? '(Required)' : ''}</>}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 rounded-xl flex gap-3" style={{ background: 'var(--color-greenBg)', border: '1px solid var(--color-greenBorder)' }}>
                <i className="fas fa-shield-alt text-base" style={{ color: 'var(--color-green)' }} />
                <div className="text-[10px] leading-relaxed" style={{ color: 'var(--color-textMuted)' }}>
                  <b style={{ color: 'var(--color-text)' }}>Step 1:</b> Upload all required documents (marked with <span style={{ color: 'var(--color-red)' }}>*</span>)<br />
                  <b style={{ color: 'var(--color-text)' }}>Step 2:</b> Click "Submit for Verification"<br />
                  <b style={{ color: 'var(--color-text)' }}>Accepted formats:</b> JPG, PNG, PDF (max 10MB)
                </div>
              </div>

              {!allVerified && (
                <button onClick={handleSubmitVerification} disabled={!canSubmit || isSubmitting || isUploading}
                  className="w-full flex items-center justify-center gap-2 py-3 mt-6 rounded-full font-syne text-sm font-bold transition-all"
                  style={{ background: canSubmit ? 'var(--color-gradientOrange)' : 'var(--color-bgCard)', color: canSubmit ? 'var(--color-textInverse)' : 'var(--color-textDim)' }}>
                  {isSubmitting ? <><span className="inline-block w-3 h-3 border-2 rounded-full animate-spin mr-1" style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} /> Submitting...</>
                    : !allRequiredUploaded ? <><i className="fas fa-lock mr-1" /> Upload All Required Documents First ({uploadedRequiredCount}/{requiredTypes.length})</>
                    : <><i className="fas fa-paper-plane mr-1" /> Submit for Verification</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Toast />
      <style>{`
        @keyframes float { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(28px, -28px); } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-float { animation: float 20s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: -8s; }
        .animate-slide-up { animation: slide-up 0.6s ease-out both; }
        .animation-delay-100 { animation-delay: 0.1s; }
      `}</style>
    </div>
  );
};

export default Documents;